import {Landlord, LandlordTypeOptions} from '@landlords/models';
import type {PropertyUnitPeriod} from '@periods/models/property.unit.period.interface';
import {getLandlordDisplayName} from '@units/pipes/landlordDisplayName.pipe';
import {resolveTaxProfile} from '@vat/engine/tax-profile';
import type {LandlordTaxProfile, VatReturn, VatTaxStatus} from '@vat/engine/types';
import {normalizeToValidDateOrNull} from 'app/helpers';
import {VAT_LANDING_ROW_COPY, VAT_PERIOD_TYPE_LABELS} from '../../vat-copy';
import {buildPeriodCards, nextDeadline, VatPeriodCard} from '../cockpit/vat-cockpit.util';

/**
 * Reine Landing-Logik (Spec 6 Screen 1): je Eigentümer Status, Setup-Zustand, offene Zeiträume des
 * laufenden Jahres und nächste Frist – ohne Live-Berechnung (nur vorhandene vatReturns-Dokumente).
 *
 * Jahreswechsel (Review C5): Der letzte Voranmeldungszeitraum des Vorjahres (M12/Q4, mit Dauerfrist auch M11) ist erst
 * im Januar/Februar/März fällig. `nextDue`/`overdue` berücksichtigen deshalb zusätzlich den „Überhang“ des Vorjahres –
 * genau die Vorjahres-Zeiträume, deren gesetzliche Frist im laufenden Jahr liegt. `openCount` bleibt spec-konform
 * (1.3 „Zahl offener Zeiträume im Jahr“) auf das laufende Jahr beschränkt.
 *
 * Review-Befund C: `quiet` (Kleinunternehmer/steuerfrei) gilt zum Stichtag `now`. Wer zum Jahreswechsel von
 * Regelbesteuerung zu Kleinunternehmer wechselt, schuldet Q4/M12 des Vorjahres trotzdem – der Überhang wird deshalb
 * nur vom offenen Setup gated, den Vorjahres-Status prüft `buildPreviousYearCarryOverCards` selbst.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
/** Anzeigename ohne Rand-Leerzeichen (der Pipe-Helfer setzt bei Firmen ohne Personenname ein führendes Leerzeichen). */
const displayNameOf = (landlord: Landlord): string => getLandlordDisplayName(landlord).trim();

export interface VatLandlordCardView {
    landlord: Landlord;
    name: string;
    profile: LandlordTaxProfile;
    status: VatTaxStatus;
    setupOpen: boolean;
    quiet: boolean;
    year: number;
    openCount: number;
    nextDue: VatPeriodCard | null;
}

/**
 * Überhang des Vorjahres: Zeitraum-Karten für `year - 1`, deren gesetzliche Frist (`due.dueDate`) erst im Jahr `year`
 * liegt – M12/Q4 (10.01.), mit Dauerfrist M11 (10.01.) und M12/Q4 (10.02./10.03.). Das Steuerprofil wird zum 31.12. des
 * Vorjahres aufgelöst (`besteuerung`-Historie): war der Eigentümer im Vorjahr noch Kleinunternehmer/steuerfrei, gibt es
 * keinen Überhang. Frühere Vorjahres-Zeiträume (Frist noch im Vorjahr) bleiben Sache des Cockpits – sonst wäre jeder im
 * laufenden Jahr eingerichtete Eigentümer für ein nie in Nahaus geführtes Jahr dauerhaft „überfällig“.
 */
export const buildPreviousYearCarryOverCards = (landlord: Landlord, docs: VatReturn[], year: number, now: Date): VatPeriodCard[] => {
    const previousYear = year - 1;
    const profile = resolveTaxProfile(landlord?.taxProfile, new Date(previousYear, 11, 31));
    if (profile.status !== 'REGELBESTEUERUNG') {
        return [];
    }
    return buildPeriodCards({landlordID: landlord.id, profile, docs, year: previousYear, now})
        .filter(card => card.due !== null && card.due.dueDate.getFullYear() === year);
};

/**
 * Landing-Karte je Eigentümer (Spec 6 Screen 1): Status-Pill, nächste Frist, offene Zeiträume des laufenden Jahres.
 * `docs` darf Dokumente des laufenden Jahres UND des Vorjahres enthalten (`buildPeriodCards` filtert je Jahr).
 */
export const buildLandlordCardView = (landlord: Landlord, docs: VatReturn[], now: Date): VatLandlordCardView => {
    const profile = resolveTaxProfile(landlord?.taxProfile, now);
    const setupOpen = profile.setupCompletedAt === null;
    const quiet = profile.status !== 'REGELBESTEUERUNG';
    const year = now.getFullYear();
    const cards = quiet || setupOpen ? [] : buildPeriodCards({landlordID: landlord.id, profile, docs, year, now});
    // Überhang auch bei `quiet` (Befund C): der Vorjahres-Status entscheidet, nicht der zu `now` – nur das Setup-Gate greift.
    const carryOver = setupOpen ? [] : buildPreviousYearCarryOverCards(landlord, docs, year, now);
    return {
        landlord,
        name: displayNameOf(landlord),
        profile,
        status: profile.status,
        setupOpen,
        quiet,
        year,
        // Spalte „Offen“ = laufendes Jahr (Spec 1.3, Tooltip nennt das Jahr); der Überhang zeigt sich in nextDue/overdue
        openCount: cards.filter(card => card.status === 'OFFEN' || card.status === 'ENTWURF').length,
        // auch bei `quiet`: bei Wechsel zu Kleinunternehmer bleibt der Vorjahres-Überhang die nächste (ggf. überfällige) Frist
        nextDue: nextDeadline([...carryOver, ...cards]),
    };
};

export const sortLandlordCardViews = (views: VatLandlordCardView[]): VatLandlordCardView[] =>
    [...views].sort((a, b) => a.name.localeCompare(b.name, 'de'));

// ---------------------------------------------------------------------------
// Landing v2 – Arbeitsliste (Spec 2026-09-13 Sektion 1, Plan Task A1)
// ---------------------------------------------------------------------------

export type VatLandingStatusFilter = 'ALLE' | 'REGELBESTEUERUNG' | 'KLEINUNTERNEHMER' | 'STEUERFREI' | 'UEBERFAELLIG';

export const VAT_LANDING_STATUS_FILTERS: readonly VatLandingStatusFilter[] = ['ALLE', 'REGELBESTEUERUNG', 'KLEINUNTERNEHMER', 'STEUERFREI', 'UEBERFAELLIG'];

/** Tabellenzeile der Landing v2: Kartendaten plus Typ, Ort, Überfällig-Flag und Zeitraum-Label. */
export interface VatLandingRow extends VatLandlordCardView {
    typeLabel: string;
    place: string | null;
    overdue: boolean;
    periodTypeLabel: string;
}

/**
 * Vorschlag (Spec 1.1/1.2): Eigentümer ohne Steuerprofil mit aktuellen gewerblichen Mietverhältnissen, die Umsatzsteuer
 * ausweisen (B1 – der Datendienst filtert steuerpflichtige Kategorien vor). `commercialCount` = steuerpflichtige
 * Mietverhältnisse, `propertyCount` = distinct Objekte dieser Mietverhältnisse.
 */
export interface VatLandingSuggestion {
    landlord: Landlord;
    name: string;
    commercialCount: number;
    propertyCount: number;
}

/** Für das Team ausgeblendeter Vorschlag (`taxProfile.suggestionDismissed`, Landing v2 B2) – leise Rest-Zeile mit „Wieder anzeigen“. */
export interface VatLandingDismissedSuggestion {
    landlord: Landlord;
    name: string;
    dismissedAt: Date | null;
    dismissedByName: string | null;
}

export interface VatLandingSuggestionPartition {
    suggestions: VatLandingSuggestion[];
    dismissedSuggestions: VatLandingDismissedSuggestion[];
}

/**
 * Trennt ausgeblendete Vorschläge ab (`landlord.taxProfile.suggestionDismissed` gesetzt). Die Reihenfolge bleibt die der
 * Eingabe (commercialCount desc, dann Name); `at` darf ein Firestore-Timestamp sein (→ Date), fehlender `byName` → null.
 */
export const partitionDismissedSuggestions = (rows: VatLandingSuggestion[]): VatLandingSuggestionPartition => {
    const suggestions: VatLandingSuggestion[] = [];
    const dismissedSuggestions: VatLandingDismissedSuggestion[] = [];
    for (const row of rows ?? []) {
        const marker = row?.landlord?.taxProfile?.suggestionDismissed;
        if (!marker) {
            suggestions.push(row);
            continue;
        }
        const byName = typeof marker.byName === 'string' ? marker.byName.trim() : '';
        dismissedSuggestions.push({
            landlord: row.landlord,
            name: row.name,
            dismissedAt: normalizeToValidDateOrNull(marker.at),
            dismissedByName: byName || null,
        });
    }
    return {suggestions, dismissedSuggestions};
};

export type VatLandingSuggestionPeriod = Pick<PropertyUnitPeriod, 'landlordID' | 'propertyID' | 'isCommercial' | 'isCurrent'>;

/** Client-Gruppierung eines Jahres-Snapshots (`listByYear`) nach `landlordID`; Dokumente ohne landlordID fallen weg. */
export const groupReturnsByLandlord = (docs: VatReturn[]): Map<string, VatReturn[]> => {
    const grouped = new Map<string, VatReturn[]>();
    for (const doc of docs ?? []) {
        const landlordID = doc?.landlordID;
        if (!landlordID) {
            continue;
        }
        const list = grouped.get(landlordID);
        if (list) {
            list.push(doc);
        } else {
            grouped.set(landlordID, [doc]);
        }
    }
    return grouped;
};

const typeLabelOf = (landlord: Landlord): string =>
    VAT_LANDING_ROW_COPY.typeLabels[landlord?.type === LandlordTypeOptions.COMMERCIAL ? 'commercial' : 'private'];

/** Zeitraum-Spalte: Regelbesteuerung → Profilzeitraum, Kleinunternehmer/steuerfrei → „befreit“. */
const periodTypeLabelOf = (view: VatLandlordCardView): string =>
    VAT_PERIOD_TYPE_LABELS[view.quiet ? 'BEFREIT' : (view.profile.voranmeldungszeitraum ?? 'BEFREIT')];

/** Ort aus `GermanAddress.locality` (Objekt `{long, short}` der Google-Maps-Autocomplete-Lib), wie address.pipe. */
const placeOf = (landlord: Landlord): string | null =>
    landlord?.address?.locality?.long || landlord?.address?.locality?.short || null;

export const buildLandingRow = (landlord: Landlord, docs: VatReturn[], now: Date): VatLandingRow => {
    const view = buildLandlordCardView(landlord, docs, now);
    return {
        ...view,
        typeLabel: typeLabelOf(landlord),
        place: placeOf(landlord),
        overdue: view.nextDue?.dueTone === 'overdue',
        periodTypeLabel: periodTypeLabelOf(view),
    };
};

export const sortLandingRows = (rows: VatLandingRow[]): VatLandingRow[] =>
    [...rows].sort((a, b) => a.name.localeCompare(b.name, 'de'));

/**
 * Vorschläge aus dem Root-Spiegel `periods` (isCommercial && isCurrent): je Eigentümer Anzahl Mietverhältnisse und
 * distinct Immobilien; eingerichtete (`configuredIDs`) und unbekannte Eigentümer (nicht in `landlordsByID`) fallen weg.
 * Sortierung: commercialCount desc, dann Name (de).
 */
export const buildSuggestionRows = (
    periods: VatLandingSuggestionPeriod[],
    landlordsByID: Map<string, Landlord>,
    configuredIDs: Set<string>,
): VatLandingSuggestion[] => {
    const counts = new Map<string, { commercialCount: number; propertyIDs: Set<string> }>();
    for (const period of periods ?? []) {
        const landlordID = period?.landlordID;
        if (!landlordID || period.isCommercial !== true || period.isCurrent !== true) {
            continue;
        }
        if (configuredIDs.has(landlordID) || !landlordsByID.has(landlordID)) {
            continue;
        }
        const entry = counts.get(landlordID) ?? {commercialCount: 0, propertyIDs: new Set<string>()};
        entry.commercialCount++;
        if (period.propertyID) {
            entry.propertyIDs.add(period.propertyID);
        }
        counts.set(landlordID, entry);
    }
    return [...counts.entries()]
        .map(([landlordID, entry]) => {
            const landlord = landlordsByID.get(landlordID);
            return {landlord, name: displayNameOf(landlord), commercialCount: entry.commercialCount, propertyCount: entry.propertyIDs.size};
        })
        .sort((a, b) => b.commercialCount - a.commercialCount || a.name.localeCompare(b.name, 'de'));
};

const matchesStatus = (row: VatLandingRow, status: VatLandingStatusFilter): boolean => {
    switch (status) {
        case 'ALLE':
            return true;
        case 'UEBERFAELLIG':
            return row.overdue;
        default:
            return row.status === status;
    }
};

const searchHaystack = (row: VatLandingRow): string =>
    [row.name, row.landlord?.companyName ?? '', row.place ?? ''].join(' ').toLocaleLowerCase('de');

/** Client-Filter: jedes Suchwort muss in Name/Firma/Ort vorkommen (Groß-/Kleinschreibung egal) und der Status passen. */
export const filterLandingRows = (rows: VatLandingRow[], query: string, status: VatLandingStatusFilter): VatLandingRow[] => {
    const tokens = (query ?? '').trim().toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
    return (rows ?? []).filter(row => {
        if (!matchesStatus(row, status)) {
            return false;
        }
        if (!tokens.length) {
            return true;
        }
        const haystack = searchHaystack(row);
        return tokens.every(token => haystack.includes(token));
    });
};

/** Zähler-Pills der Status-Filter (ALLE = Gesamtzahl, UEBERFAELLIG quer über alle Status). */
export const landingStatusCounts = (rows: VatLandingRow[]): Record<VatLandingStatusFilter, number> => {
    const counts: Record<VatLandingStatusFilter, number> = {ALLE: 0, REGELBESTEUERUNG: 0, KLEINUNTERNEHMER: 0, STEUERFREI: 0, UEBERFAELLIG: 0};
    for (const row of rows ?? []) {
        counts.ALLE++;
        counts[row.status]++;
        if (row.overdue) {
            counts.UEBERFAELLIG++;
        }
    }
    return counts;
};
